import { useState, useRef, useEffect } from 'react'
import { useChat } from '@ai-sdk/react'
import { DefaultChatTransport } from 'ai'
import env from '@/config/env'
import { type BrainstormChatMessage } from '@backend/routes/stream/brainstorm-chat'
import { type Moodboard } from '@backend/lib/db/schema/moodboard'
import { trpc, getApiHeaders } from '@/config/trpc'
import { Button } from '@/components/ui/shadcn/button'
import { Textarea } from '@/components/ui/shadcn/textarea'
import { BrainstormMessageItem } from './brainstorm-message-item'
import { ArrowLeft, Lightbulb, RotateCcw, Send, Sparkles } from 'lucide-react'
import { GradientAnimation } from '@/components/ui/GradientAnimation'
import { UpgradeModal } from '@/components/upgrade-modal'
import { type PlatformType } from '@backend/lib/db/schema/platform'

interface Props {
  moodboard: Pick<Moodboard, 'name' | 'analysisBrief'> | null
  onBack: () => void
  onTopicFinalized: (topic: string, refinedPrompt: string) => void
}

export function BrainstormView({ moodboard, onBack, onTopicFinalized }: Props) {
  const [prompt, setPrompt] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [selectedPlatforms, setSelectedPlatforms] = useState<PlatformType[]>([])
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const isSendingRef = useRef(false)

  const { mutateAsync: fetchTrendingTopics, isPending: isFetchingTrends } =
    trpc.brainstorm.getTrendingTopics.useMutation()

  const {
    messages: chatMessages,
    status: chatStatus,
    sendMessage,
    addToolOutput,
    setMessages,
    error: chatError,
  } = useChat<BrainstormChatMessage>({
    transport: new DefaultChatTransport({
      api: `${env.VITE_BACKEND_URL}/stream/brainstorm-chat`,
      headers: getApiHeaders,
    }),
    onError: (error) => {
      console.error('Brainstorm chat error:', error)
      try {
        const parsed = JSON.parse(error.message)
        if (parsed.error === 'USAGE_LIMIT_EXCEEDED') {
          setShowUpgradeModal(true)
          isSendingRef.current = false
          return
        }
      } catch {
        // Not JSON, fall through
      }
      if (error.message?.includes('Usage limit exceeded')) {
        setShowUpgradeModal(true)
      } else {
        setError(error.message || 'An error occurred. Please try again.')
      }
      isSendingRef.current = false
    },
    onFinish: () => {
      isSendingRef.current = false
      setError(null)
    },
    onToolCall: async ({ toolCall }) => {
      if (toolCall.dynamic) return

      try {
        if (toolCall.toolName === 'getTrendingTopics') {
          // Build rich brand context from the moodboard brief for personalization
          const personaContext = buildBrandContext() || toolCall.input.personaContext

          // Call the actual tRPC endpoint to fetch real trending topics
          const result = await fetchTrendingTopics({
            platforms: toolCall.input.platforms,
            industry: toolCall.input.industry,
            focus: toolCall.input.focus,
            personaContext,
          })

          // Update selected platforms from the tool call
          setSelectedPlatforms(toolCall.input.platforms)

          addToolOutput({
            tool: 'getTrendingTopics',
            toolCallId: toolCall.toolCallId,
            output: {
              trends: result.trends,
              sources: result.sources,
            },
          })
        } else if (toolCall.toolName === 'finalizeTopic') {
          addToolOutput({
            tool: 'finalizeTopic',
            toolCallId: toolCall.toolCallId,
            output: { success: true },
          })
        }
      } catch (error) {
        console.error('Error handling tool call:', error)
        setError('An error occurred while fetching trends. Please try again.')
        // Provide fallback output so the conversation can continue
        if (toolCall.toolName === 'getTrendingTopics') {
          addToolOutput({
            tool: 'getTrendingTopics',
            toolCallId: toolCall.toolCallId,
            output: {
              trends: [],
              sources: [],
            },
          })
        }
      }
    },
  })

  // Scroll to bottom on new messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [chatMessages, chatStatus, isFetchingTrends])

  // Handle chat errors
  const [prevChatError, setPrevChatError] = useState(chatError)
  if (prevChatError !== chatError && chatError) {
    setPrevChatError(chatError)
    setError(chatError.message || 'An error occurred. Please try again.')
  }
  // Keep ref in sync outside of render (ref writes during render are not allowed)
  useEffect(() => {
    if (chatError) {
      isSendingRef.current = false
    }
  }, [chatError])

  const handleSendMessage = async (messageText?: string) => {
    const textToSend = messageText || prompt.trim()
    if (!textToSend) return

    if (isSendingRef.current || chatStatus === 'streaming') return

    if (!messageText) {
      setPrompt('')
    }

    setError(null)
    isSendingRef.current = true

    try {
      const cleanBrief = moodboard?.analysisBrief
        ? JSON.parse(JSON.stringify(moodboard.analysisBrief))
        : null

      await sendMessage(
        { text: textToSend },
        {
          body: {
            context: {
              moodboardBrief: cleanBrief,
              selectedPlatforms: selectedPlatforms.length > 0 ? selectedPlatforms : undefined,
            },
          },
        },
      )
    } catch (error) {
      console.error('Error sending message:', error)
      setError(error instanceof Error ? error.message : 'Failed to send message. Please try again.')
      isSendingRef.current = false
    }
  }

  const handleClearChat = () => {
    setMessages([])
    setPrompt('')
    setError(null)
    setSelectedPlatforms([])
  }

  const handleTopicSelect = (topic: string, refinedPrompt: string) => {
    onTopicFinalized(topic, refinedPrompt)
  }

  const handleContentIdeaSelect = (topic: string, contentIdea: string) => {
    // Send a message to the agent to finalize this specific content idea
    const message = `I want to create a post about "${topic}" with this angle: "${contentIdea}". Please finalize this topic for me.`
    handleSendMessage(message)
  }

  const handleBrainstormMore = (topic: string) => {
    const message = `Give me more content ideas for the topic "${topic}". Suggest different angles and approaches I haven't seen yet.`
    handleSendMessage(message)
  }

  // Build brand context string from the moodboard brief for better trending
  // topic personalization. Compact: brainstorm only needs the gist, not the
  // full labeled-sections prompt the generators use.
  const buildBrandContext = (): string | undefined => {
    if (!moodboard) return undefined

    const parts: string[] = []
    if (moodboard.name) parts.push(`Brand: ${moodboard.name}`)
    for (const block of moodboard.analysisBrief?.blocks ?? []) {
      if (block.type === 'throughline') parts.push(block.text)
      if (block.type === 'audiences' && block.cards.length > 0)
        parts.push(`Target audience: ${block.cards.map((c) => c.title).join(', ')}`)
      if (block.type === 'keyPhrases' && block.phrases.length > 0)
        parts.push(`Key phrases: ${block.phrases.join(', ')}`)
    }

    return parts.length > 0 ? parts.join('. ') : undefined
  }

  const isStreaming = chatStatus === 'streaming'
  const hasMessages = chatMessages.length > 0

  const suggestions = [
    {
      text: "I don't know what to post",
      message: "I'm not sure what to post about today. Can you help me brainstorm?",
    },
    {
      text: "What's trending?",
      message: 'What topics are trending that I could post about?',
    },
    {
      text: 'I have a rough idea',
      message: "I have a rough idea I'd like to explore and refine",
    },
  ]

  return (
    <div className="flex h-full min-h-screen flex-col">
      <GradientAnimation animationDuration="25s" reactive />

      {/* Header */}
      <div className="animate-fade-down animate-once animate-duration-500 animate-ease-in border-border/60 bg-background/80 backdrop-blur-xs relative z-10 border-b">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="text-muted-foreground hover:text-foreground">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <div className="flex items-center gap-2">
            <div className="bg-primary/10 flex h-8 w-8 items-center justify-center rounded-full">
              <Lightbulb className="text-primary h-4 w-4" />
            </div>
            <span className="text-foreground font-medium">Brainstorm Mode</span>
          </div>
          <div className="w-[72px]" /> {/* Spacer for centering */}
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex flex-1 flex-col">
        <div className="mx-auto flex w-full max-w-4xl flex-1 flex-col px-4 py-6">
          {/* Chat Area */}
          <div className="flex-1 overflow-y-auto">
            {error && (
              <div className="mb-4 rounded-lg border border-red-500/50 bg-red-500/10 p-3 text-sm text-red-600 dark:text-red-400">
                <div className="font-semibold">Error</div>
                <div>{error}</div>
                <button onClick={() => setError(null)} className="mt-2 text-xs underline">
                  Dismiss
                </button>
              </div>
            )}

            {hasMessages ? (
              <div className="space-y-4">
                {chatMessages.map((message) => {
                  if (!message || !message.id) return null
                  return (
                    <BrainstormMessageItem
                      key={message.id}
                      message={message}
                      onTopicSelect={handleTopicSelect}
                      onContentIdeaSelect={handleContentIdeaSelect}
                      onBrainstormMore={handleBrainstormMore}
                      isStreaming={isStreaming}
                    />
                  )
                })}
                {isFetchingTrends && (
                  <div className="flex justify-start">
                    <div className="border-border bg-card rounded-lg border p-4">
                      <div className="flex items-center gap-3">
                        <div className="border-primary h-5 w-5 animate-spin rounded-full border-2 border-t-transparent" />
                        <span className="text-muted-foreground text-sm">
                          Scanning news sources for trending topics...
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            ) : (
              <div className="flex h-full flex-col items-center justify-center py-12 text-center">
                <div className="animate-fade-down animate-once animate-duration-500 animate-delay-100 animate-ease-in bg-primary/10 mb-6 flex h-20 w-20 items-center justify-center rounded-full">
                  <Sparkles className="text-primary h-10 w-10" />
                </div>
                <div className="animate-fade-down animate-once animate-duration-500 animate-delay-200 animate-ease-in">
                  <h2 className="text-foreground mb-3 text-2xl font-semibold">
                    Let's find your next post
                  </h2>
                  <p className="text-muted-foreground mb-8 max-w-md text-base">
                    Tell me what's on your mind, or I can help you discover trending topics in your
                    industry. We'll refine it together until you're excited to create.
                  </p>
                </div>
                <div className="flex flex-wrap justify-center gap-3">
                  {suggestions.map((suggestion, index) => (
                    <div
                      key={suggestion.text}
                      className="animate-fade-left animate-once"
                      style={{ animationDelay: `${300 + index * 100}ms` }}>
                      <Button
                        variant="secondary"
                        size="lg"
                        className="text-base"
                        onClick={() => handleSendMessage(suggestion.message)}>
                        {suggestion.text}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Input Area */}
          <div className="animate-fade-down animate-once animate-duration-500 animate-delay-300 animate-ease-in mt-6">
            <div className="border-border hover:border-primary focus-within:border-primary bg-card flex flex-col gap-3 rounded-xl border p-4 transition-colors">
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    if (prompt.trim() && !isStreaming && !isFetchingTrends) {
                      handleSendMessage()
                    }
                  }
                }}
                placeholder="What's on your mind? Share an idea, ask for trends, or tell me about your business..."
                className="bg-card text-foreground placeholder:text-muted-foreground focus:outline-hidden min-h-[80px] resize-none border-none"
              />
              <div className="flex w-full items-center justify-between">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground"
                  onClick={handleClearChat}
                  disabled={!hasMessages || isStreaming || isFetchingTrends}>
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Start fresh
                </Button>

                <Button
                  size="default"
                  onClick={() => handleSendMessage()}
                  disabled={!prompt.trim() || isStreaming || isFetchingTrends}>
                  <Send className="mr-2 h-4 w-4" />
                  Send
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <UpgradeModal
        open={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        limitType="chat"
        surface="home_brainstorm"
      />
    </div>
  )
}
